import { useState } from 'react';
import styled from '@emotion/styled';
import { Button, Field, Modal, Stack, TextInput, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import {
  EVENT_NAMES,
  EVENT_TRIGGERED_ON,
  EVENT_TRIGGERS,
  EVENT_UI_POSITIONS,
} from '@hotjar/webapp/domains/heatmaps/tracking';
import type { TrackingSite } from '@hotjar/webapp/tracking/utils';
import { trackPinnedPage } from '@hotjar/heatmaps';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { noop } from '@hotjar/webapp/lib/function';
import type { Site } from '@hotjar/site';
import { usePinnedPages } from '@hotjar/webapp/domains/heatmaps/hooks/usePinnedPages';
import type { GlobalFilter } from '@hotjar/global-filters';
import { convertFromNewUiToQueryFormat } from '@hotjar/global-filters';

const StyledField = styled(Field)`
  width: unset;
`;

const Error = styled.p`
  color: ${tokens.color.text_danger};
  margin: ${tokens.spacer.x0} 0;
  font-size: 12px;
`;

interface NewPinModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSite: Site | undefined;
  pattern: string;
  matchOperation: TargetingValue;
  filters: GlobalFilter;
}

const nameId = 'new-pin-name';

export const NewPinModal = ({
  isOpen,
  onClose,
  currentSite,
  pattern,
  matchOperation,
  filters,
}: NewPinModalProps): JSX.Element => {
  const siteId = currentSite?.id as number;

  const { t } = useTranslation('heatmaps');
  const track = useTrack();
  const { success, error } = useNotify();
  const [isLoading, setLoading] = useState(false);

  const [name, setName] = useState('');
  const [nameErrorMessage, setNameErrorMessage] = useState('');

  const jsonQueryString = JSON.stringify(convertFromNewUiToQueryFormat(filters));

  const { addPin } = usePinnedPages({
    siteId,
    pattern,
    matchOperation,
    jsonQueryString,
  });

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNameErrorMessage('');
    setName(event.target.value);
  };

  const validateNameInput = (value: string): string => {
    let errorMessage;

    if (!value) {
      errorMessage = t('save-heatmap-modal.required-name');
    } else if (!value.trim().length) {
      errorMessage = t('save-heatmap-modal.invalid-name');
    } else {
      errorMessage = '';
    }

    return errorMessage;
  };

  const pinPage = (successCallback = noop) => {
    track.click(
      EVENT_NAMES.CREATE_HEATMAP,
      EVENT_UI_POSITIONS.HEADER,
      track.getGroupDataForSite(currentSite as TrackingSite),
      {
        state_event_was_triggered_on: EVENT_TRIGGERED_ON.HEADER,
      },
    );
    return addPin.mutateAsync(
      {
        siteId,
        name,
        pattern,
        matchOperation,
        jsonQueryString,
      },
      {
        onSuccess: (newPin: HeatmapAPI.PinnedPage) => {
          if (newPin.id) {
            success(t('save-heatmap-modal.creation-success'));
            trackPinnedPage({
              ...newPin,
              trigger: EVENT_TRIGGERS.SAVE_BUTTON,
              displayType: 'screenshot',
              eventType: 'created',
              siteId,
            });
            successCallback();
          }
        },
        onError: () => {
          error(t('save-heatmap-modal.creation-error'));
          onClose();
        },
      },
    );
  };

  const handleSubmit = async () => {
    const nameError = validateNameInput(name);
    setNameErrorMessage(nameError);

    if (nameError) {
      return;
    }

    setLoading(true);
    pinPage(() => {
      setLoading(false);
      onClose();
    });
  };

  return (
    <Modal
      appElementSelector="#root"
      contentLabel={t('save-heatmap-modal.content-label')}
      isOpen={isOpen}
      onClose={onClose}
      size="m"
    >
      <Modal.Header title={t('save-heatmap-modal.title')} />
      <Modal.Body>
        <Stack direction="column" preset="form">
          <StyledField labelText={t('save-heatmap-modal.name')} inputId={nameId}>
            <TextInput
              value={name}
              onChange={handleNameChange}
              placeholder={t('save-heatmap-modal.name-placeholder')}
            />
            {!!nameErrorMessage && <Error>{nameErrorMessage}</Error>}
          </StyledField>
        </Stack>
      </Modal.Body>

      <Modal.Footer>
        <Button isDisabled={false} kind="ghost" onClick={onClose}>
          {t('save-heatmap-modal.cancel')}
        </Button>
        <Button kind="primary" onClick={handleSubmit} isLoading={isLoading}>
          {t('save-heatmap-modal.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
